import React, { useState } from 'react';
import { PlanFeature } from '../types';

interface PlanFeatureFormProps {
  planId: number;
  initialData?: PlanFeature | null;
  onSubmit: () => void;
  onCancel: () => void;
}

interface PlanFeatureCreate {
  plan_id: number;
  feature_type: 'FEATURE' | 'DOCUMENT_ACCESS' | 'AUDIT_ACCESS';
  name: string;
  description?: string;
  trial_period_days?: number;
  value_type: 'BOOLEAN' | 'NUMERIC';
  numeric_limit?: number | null;
  valid_from: string;
  valid_to?: string;
  is_active?: boolean;
}

const PlanFeatureForm: React.FC<PlanFeatureFormProps> = ({
  planId,
  initialData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<PlanFeatureCreate>({
    plan_id: planId,
    feature_type: initialData?.feature_type || 'FEATURE',
    name: initialData?.name || '',
    description: initialData?.description || '',
    trial_period_days: initialData?.trial_period_days || 0,
    value_type: initialData?.value_type || 'BOOLEAN',
    numeric_limit: initialData?.numeric_limit || null,
    valid_from: initialData?.valid_from 
      ? new Date(initialData.valid_from).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0],
    valid_to: initialData?.valid_to 
      ? new Date(initialData.valid_to).toISOString().split('T')[0]
      : '',
    is_active: initialData?.is_active !== undefined ? initialData.is_active : true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
               type === 'number' ? Number(value) : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.valid_from) {
      newErrors.valid_from = 'Valid from date is required';
    }

    if (formData.valid_to && formData.valid_to <= formData.valid_from) {
      newErrors.valid_to = 'Valid to date must be after valid from date';
    }

    if (formData.trial_period_days && formData.trial_period_days < 0) {
      newErrors.trial_period_days = 'Trial period cannot be negative';
    }

    if (formData.value_type === 'NUMERIC' && formData.numeric_limit !== null && formData.numeric_limit !== undefined) {
      if (formData.numeric_limit < 0) {
        newErrors.numeric_limit = 'Numeric limit cannot be negative';
      }
      if (formData.numeric_limit === 0) {
        newErrors.numeric_limit = 'Numeric limit must be greater than 0';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);
      
      // Since we don't have a direct API endpoint for plan features,
      // we'll simulate the API call here. In a real implementation,
      // you would call the appropriate API endpoint.
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSubmit();
    } catch (error) {
      console.error('Error saving plan feature:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <label className="form-label">Feature Type *</label>
        <select
          name="feature_type"
          value={formData.feature_type}
          onChange={handleChange}
          className="form-control"
        >
          <option value="FEATURE">Feature</option>
          <option value="DOCUMENT_ACCESS">Document Access</option>
          <option value="AUDIT_ACCESS">Audit Access</option>
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Value Type *</label>
        <select
          name="value_type"
          value={formData.value_type}
          onChange={handleChange}
          className="form-control"
        >
          <option value="BOOLEAN">Boolean (Yes/No)</option>
          <option value="NUMERIC">Numeric (Limit/Quantity)</option>
        </select>
      </div>

      {formData.value_type === 'NUMERIC' && (
        <div className="form-group">
          <label className="form-label">Numeric Limit</label>
          <input
            type="number"
            name="numeric_limit"
            value={formData.numeric_limit || ''}
            onChange={handleChange}
            className="form-control"
            min="0"
            placeholder="Enter numeric limit"
          />
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`form-control ${errors.name ? 'error' : ''}`}
          placeholder="Enter feature name"
        />
        {errors.name && <div className="form-error">{errors.name}</div>}
      </div>

      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          className="form-control"
          rows={3}
          placeholder="Enter feature description"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Trial Period (Days)</label>
        <input
          type="number"
          name="trial_period_days"
          value={formData.trial_period_days}
          onChange={handleChange}
          className={`form-control ${errors.trial_period_days ? 'error' : ''}`}
          min="0"
          placeholder="0"
        />
        {errors.trial_period_days && <div className="form-error">{errors.trial_period_days}</div>}
      </div>

      <div className="form-row">
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Valid From *</label>
            <input
              type="date"
              name="valid_from"
              value={formData.valid_from}
              onChange={handleChange}
              className={`form-control ${errors.valid_from ? 'error' : ''}`}
            />
            {errors.valid_from && <div className="form-error">{errors.valid_from}</div>}
          </div>
        </div>
        <div className="form-col">
          <div className="form-group">
            <label className="form-label">Valid To</label>
            <input
              type="date"
              name="valid_to"
              value={formData.valid_to}
              onChange={handleChange}
              className={`form-control ${errors.valid_to ? 'error' : ''}`}
            />
            {errors.valid_to && <div className="form-error">{errors.valid_to}</div>}
          </div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">
          <input
            type="checkbox"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
            style={{ marginRight: '8px' }}
          />
          Active
        </label>
      </div>

      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Saving...' : (initialData ? 'Update' : 'Create')}
        </button>
      </div>
    </form>
  );
};

export default PlanFeatureForm;